<template>
	<div>
		<base-header class="pb-6 bg-light">
			<b-row align-v="center" class="py-4">
				<b-col cols="12" lg="6">
					<h6 class="h2 d-inline-block mb-0 text-dark">OTA Report</h6>
				</b-col>
				<b-col cols="12" lg="6" class="text-right">
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</b-col>
			</b-row>
		</base-header>
		<b-card>
			<b-card-body>
				<b-row class="px-4 pt-4 align-items-center" style="gap: 3rem">
					<p class="font-weight-bold m-0">OTA Inventory Details</p>

					<b-dropdown
						class="float-right mr-2"
						size="sm"
						variant="primary"
            menu-class="card-body p-0 m-0"
					>
						<template #button-content> 
							Export {{ totalRows }} records 
						</template>
            <b-dropdown-item @click="exportData('csv')"> 
              Export as CSV 
            </b-dropdown-item>
            <b-dropdown-item @click="exportData('xls')">
              Export as XLS (Excel)
            </b-dropdown-item>
          </b-dropdown>

					<b-button
						@click="getReport"
						:disabled="showErrorMessage"
						variant="primary"
						size="sm"
						class="rounded"
            data-cy="ota-reports-search-btn"
					>
						<i class="fas fa-search"></i> Search</b-button
					>
				</b-row>

				<b-row class="px-4 pt-4 d-flex align-items-start justify-content-between" style="gap: 3rem">
					<b-col style="width: 100%">
						<label for="Carrier" class="text-sm">Carrier:</label>
						<b-form-select
							v-model="otaReportForm.carrier"
							id="Carrier"
							value-field="value"
							text-field="text"
							style="max-width: 300px; width: 100%"
							:options="carrierOptions"
						></b-form-select>
					</b-col>

					<b-col style="width: 100%">
						<label for="Detail" class="text-sm">Detail:</label>
						<b-form-select
							v-model="otaReportForm.detail"
							id="Detail"
							value-field="value"
							text-field="text"
							style="max-width: 300px; width: 100%"
							:options="detailOptions"
						></b-form-select>
					</b-col>

					<b-col style="width: 100%">
						<label for="fromDate" class="text-sm">Start Date:</label>
						<b-form-input
							v-model="otaReportForm.start_date"
							size="sm"
							type="date"
							id="fromDate"
							style="max-width: 300px; width: 100%; height: 43px"
						/>
						<b-form-invalid-feedback :state="!showErrorMessage">
							{{ errorMessage }}
						</b-form-invalid-feedback>
					</b-col>

					<b-col style="width: 100%">
						<label for="toDate" class="text-sm">End Date:</label>
						<b-form-input
							v-model="otaReportForm.end_date"
							size="sm"
							type="date"
							id="toDate"
							style="max-width: 300px; width: 100%; height: 43px"
						/>
					</b-col>
				</b-row>
			</b-card-body>
		</b-card>

		<b-card>
			<b-card-body>
				<b-row>
					<b-col cols="12" xl="8" lg="7" md="7" sm="6">
						<b-form-group
							label=""
							label-for="per-page-select"
							label-cols-sm="0"
							label-cols-md="0"
							label-cols-lg="0"
							label-align-sm="center"
							label-size="sm"
							class="mb-2 per-page-select"
						>
							<label for="per-page-select" class="mr-2 text-sm mt-1 mr-4">Show</label>
							<b-form-select
								id="per-page-select"
								class="diameter-report-select"
								v-model="perPage"
								:options="pageOptions"
								size="sm"
								@input="changePageOption(perPage)"
							></b-form-select>
						</b-form-group>
					</b-col>
					<b-col cols="12" xl="4" lg="5" md="5" sm="6" class="customer-table-select">
						<b-form-group
							label=""
							label-for="filter-input"
							label-align-sm="right"
							label-size="sm "
							class="mb-0 per-page-select"
						>
							<label for="filter-input" class="text-sm mr-2 mt-1">Search:</label>
							<b-input-group size="sm">
								<b-form-input id="filter-input2" v-model="filter" class="mb-4"></b-form-input>
							</b-input-group>
						</b-form-group>
					</b-col>
				</b-row>

				<b-row>
					<b-col xl="12">
						<b-table
							id="reportTable"
							head-row-variant="primary-table"
							class="primary-table w-auto"
							sticky-header="true"
							:items="items"
							:fields="prevSearch.detail === 0 ? reportColumnsStandard : reportColumnsDetailed"
							:current-page="currentPage"
							:per-page="perPage"
							:filter="filter"
							:filter-included-fields="filterOn"
							:sort-by.sync="sortBy"
							:sort-desc.sync="sortDesc"
							show-empty
							responsive
							striped
							hover
							outlined
						>
						<template #empty="scope">
							<div v-if="submitLoading" class="text-center">
								<b-icon icon="three-dots" animation="cylon" font-scale="4"/>
							</div>
							<div v-else class="text-center">
								<b>{{ scope.emptyText }}</b>
							</div>
						</template>
						</b-table>

						<div>
							<br />
							<b-container class="text-xs">
								<b-row class="">
									<b-col xl="4" md="12" class="text-xl-left text-center mb-3">{{
										resultsFound
									}}</b-col>
									<b-col xl="4" md="12" class="text-center">
										<b-pagination
											v-model="currentPage"
											:total-rows="totalRows"
											:per-page="perPage"
											:current-page="currentPage"
											aria-controls="inventoryTable"
											class="mb-3 justify-content-center"
										></b-pagination>
									</b-col>
									<b-col xl="4" md="12" class="text-xl-right text-center mb-3">{{
										resultsUpdated
									}}</b-col>
								</b-row>
							</b-container>
						</div>
					</b-col>
				</b-row>
			</b-card-body>
		</b-card>
	</div>
</template>

<script>
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import pagination from "@/common/format/pagination";
import otaDashboardService from "../../../common/api-services/ota-dashboard-service";
import moment from "moment";
import Toast from "../../Components/Toast.vue";
import initService from "../../../common/api-services/init-service";
import {exportToCSV, exportToExcel} from "../../../common/export-to-csv";

export default {
	name: "otaReport",
	components: {
		Toast,
		RouteBreadCrumb,
	},
	data() {
		return {
			otaReportForm: {
				carrier: "",
				detail: 0,
				start_date: "",
				end_date: "",
			},
			prevSearch: {
				carrier: "",
				detail: 0,
				start_date: "",
				end_date: "",
			},
			submitLoading: true,
			detailLookup: {
				Standard: 0,
				Detailed: 1,
			},
			carrierOptions: [],
			items: [],
			reportDataStandard: [],
			reportColumnsStandard: [
				{key: "carriers", label: "Carrier", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
				{
					key: "provisioning_changes",
					label: "Changes",
					sortable: true,
					sortDirection: "desc",
					thClass: "bg-primary",
				},
				{key: "ota_checkins", label: "Check Ins", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
				{key: "ota_writes", label: "Writes", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
			],
			reportDataDetailed: [],
			reportColumnsDetailed: [
				{key: "date", label: "Date", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
				{key: "iccid", label: "ICCID", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
				{key: "carriers", label: "Carrier", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
				{
					key: "provisioning_changes",
					label: "Changes",
					sortable: true,
					sortDirection: "desc",
					thClass: "bg-primary",
				},
				{key: "ota_checkins", label: "Check Ins", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
				{key: "ota_writes", label: "Writes", sortable: true, sortDirection: "desc", thClass: "bg-primary"},
			],
			showErrorMessage: false,
			errorMessage: "",
			showStandardTable: true,
			showDetailedTable: false,

			totalRows: 0,
			resultsFound: "",
			resultsUpdated: "",
			currentPage: 1,
			perPage: 10,
			fromPage: 10,
			pageOptions: [10, 25, 50, 100],
			sortBy: "",
			sortDesc: false,
			sortDirection: "asc",
			filter: null,
			filterOn: [],

			//ACCESS ROLES
			isReadOnly: true,
			isEditor: false,
			isAdministrator: false,
			isPortalAdmin: false,
		};
	},

	created() {
		const currentDate = new Date();
		const thirtyDaysAgo = new Date(currentDate);
		thirtyDaysAgo.setDate(currentDate.getDate() - 30);
		const endDateFormat = currentDate.toISOString().substr(0, 10);
		const startDateFormat = thirtyDaysAgo.toISOString().substr(0, 10);
		this.otaReportForm.start_date = startDateFormat;
		this.otaReportForm.end_date = endDateFormat;
	},

	mounted() {
		document.title = "OTA - Report";

		//set the roles [isReadOnly, isEditor, isAdministrator, isPortalAdmin]
		this.isReadOnly = initService.getUserRoles().includes("OTA_READ");
		this.isEditor = initService.getUserRoles().includes("OTA_READ");
		this.isAdministrator =
			initService.getUserRoles().includes("CARRIER_ADMIN") ||
			initService.getUserRoles().includes("PORTAL_ADMIN") ||
			initService.getUserRoles().includes("OTA_ADMIN");
		this.isPortalAdmin = initService.getPortalAdminRole();
		this.carrier = initService.getPortalCarrier();

		if (this.noAccess) {
			this.$router.push("/restricted").catch(() => {});
		} else {
			//get the carrier role
			this.getCarriers().finally(() => {
				this.getReport().catch(error => {
					console.error(error);
				});
			});
		}
	},

	computed: {
		detailOptions() {
			return Object.keys(this.detailLookup).map(label => ({value: this.detailLookup[label], text: label}));
		},
		noAccess() {
			// Determine access based on menu link configuration
			let userRoles = initService.getUserRoles();
			let link = this.$store.getters["getLinkByLink"](this.$router.currentRoute.path);

			if (!link) {
				return true;
			} else {
				userRoles = userRoles.filter(role => link.roles.includes(role.toUpperCase()));
				return userRoles.length === 0;
			}
    	}
	},

	methods: {
		exportData(format) {
			let fileName = `ota-report-detailed_${moment().format("YYYY-MM-DD")}`;
			if (this.otaReportForm.detail === 0) {
				fileName = `ota-report-standard_${moment().format("YYYY-MM-DD")}`;
			}
			if (format === "xls") {
				exportToExcel(this.items, fileName);
			} else if (format === "csv") {
				exportToCSV(this.items, fileName);
			}
		},

		async getReport() {
			this.submitLoading = true;
			this.items = [];
			this.prevSearch = structuredClone(this.otaReportForm);
			let otaReportData = {
				carrier: this.otaReportForm.carrier === "" ? null : this.otaReportForm.carrier,
				// carrier: null,
				start_date: this.otaReportForm.start_date,
				// start_date: "2017-01-01",
				end_date: this.otaReportForm.end_date,
				detail: this.otaReportForm.detail,
			};
			otaDashboardService
				.otaReport(
					otaReportData.carrier,
					otaReportData.start_date,
					otaReportData.end_date,
					otaReportData.detail
				)
				.then(res => {
					// console.log(res);
					// this.items = res.data.result.report_data;
					if (!res.data.result) {
						throw new Error(res.data.error_string? res.data.error_string : "Error retrieving OTA report data");
					}
					
					let rows = {};
					res.data.result.report_data.forEach(item => {
						if (this.otaReportForm.detail === 0) {
							if (!rows.hasOwnProperty(item.carriers)) {
								rows[item.carriers] = structuredClone(item);
							} else {
								for (let key in item) {
									if (key !== "carriers") {
										rows[item.carriers][key] = parseInt(rows[item.carriers][key]) + parseInt(item[key]);
									}
								}
							}
						} else {
							this.items.push(item);
						}
					});
					if (this.otaReportForm.detail === 0) {
						for (let key in rows) {
							this.items.push(rows[key]);
						}
					}
					this.totalRows = this.items.length;
					this.resultsFound = this.totalRows + " record(s) found";
				})
				.catch(error => {
					this.$toast.error({
						component: Toast,
						props: {
							subtitle: "OTA Report",
							description: error,
						},
					});
				})
				.finally(() => {
					this.submitLoading = false;
					this.resultsUpdated = "Last Updated: " + moment().format("MM/DD/YYYY hh:mm:ss");
				});
		},

		async getCarriers() {
			this.carrierOptions = [];

			this.carrierOptions.push({value: "", text: "All"});
			this.otaReportForm.carrier = this.carrierOptions[0].value;

			// if (this.isAdministrator || this.isPortalAdmin) {
			// 	this.carrierOptions.push({value: "", text: "All"});
			// 	this.otaReportForm.carrier = this.carrierOptions[0].value;
			// } else if (this.carrierOptions.length > 0) {
			// 	const admCarrier = initService.getPortalCarrier();
			// 	this.carrierOptions.forEach(item => {
			// 		if (
			// 			item.text &&
			// 			admCarrier &&
			// 			this.otaReportForm.carrier !== "" &&
			// 			item.text.toLowerCase().includes(admCarrier.toLowerCase())
			// 		) {
			// 			this.otaReportForm.carrier = item.value;
			// 		}
			// 	});
			// } else {
			// 	this.otaReportForm.carrier =
			// 		process.env.VUE_APP_CLIENT === "uetn" ? "uetn" : initService.getPortalCarrier();
			// }

			const data = JSON.stringify({add: 0, files: 1});

			try {
				const res = await otaDashboardService.getCarriersList(data);
				this.fetchCarrierList(res);
			} catch (error) {
				console.error("Error fetching carrier list:", error);
			}
		},

		fetchCarrierList(res) {
			let selectedVal = "";

			if (res.data.result) {
				const formattedCarrier = this.otaReportForm.carrier
					? this.otaReportForm.carrier.toLowerCase().replace(" ", "").replace("_", "")
					: "";

				res.data.result.carriers.forEach(item => {
					if (item.name !== "") {
						if (this.otaReportForm.carrier === "") {
							this.carrierOptions.push({value: item.name, text: item.display_name});
						} else {
							// Only add the carrier if it matches the selected carrier
							if (
								item.name.includes(formattedCarrier) ||
								item.display_name
									.toLowerCase()
									.replace(" ", "")
									.replace("_", "")
									.includes(formattedCarrier)
							) {
								this.carrierOptions.push({value: item.name, text: item.display_name});
								selectedVal = item.name;
							}
						}
					}
				});
			}

			if (selectedVal !== "") {
				this.otaReportForm.carrier = selectedVal;
			}
		},

		getTotalRows() {
			if (this.perPage > this.totalRows) {
				this.fromPage = this.totalRows;
			} else {
				this.fromPage = this.perPage;
			}
		},

		changePageOption(page) {
			this.getTotalRows();
		},
		startPage() {
			return pagination.startPageFunc(this.currentPage, this.perPage);
		},
		lastPage() {
			return pagination.lastPageFunc(this.currentPage, this.perPage, this.totalRows);
		},
		onFiltered(filteredItems) {
			this.totalRows = filteredItems.length;
			this.currentPage = 1;
		},

		validateDateRange() {
			const startDate = new Date(this.otaReportForm.start_date);
			const endDate = new Date(this.otaReportForm.end_date);

			if (startDate > endDate) {
				this.showErrorMessage = true;
				this.errorMessage = "Start date cannot be greater than end date";
			} else {
				this.showErrorMessage = false;
				this.dateRangeError = "";
			}
		},
	},
	watch: {
		"otaReportForm.start_date": "validateDateRange",
		"otaReportForm.end_date": "validateDateRange",
	},
};
</script>

<style lang="scss" scoped>
.error-message {
	color: red;
	margin-top: 10px;
}
</style>
